import React, {useEffect, useState} from 'react';
import {useForm, useFormState} from 'react-final-form';
import isEmpty from 'is-empty';
import AceEditor from 'react-ace';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/theme-github';
import 'ace-builds/src-noconflict/ext-language_tools';

import {Box, Card, Typography} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

import {Color} from 'spectra-logic-ui/colors';
import {Checkbox, SingleChipSelectGroupField, WizardPage} from 'spectra-logic-ui/components';
import Grid from '@mui/material/Grid';

type Props = {
  title: string;
  disableSubmit: boolean;
  setDisableSubmit: (disable: boolean) => any;
}

const useStyles = makeStyles((theme) => ({
  link: {
    color: theme.palette.primary.light,
    textDecoration: 'underline',
  },
  noteText: {
    fontStyle: 'italic',
    marginBottom: 10,
  },
  error: {
    fontSize: 12,
    fontWeight: 'bold',
    color: Color.WHITE,
    background: Color.ERROR,
    padding: '5px 10px',
  },
}));

const blockPublicPolicyTip = 'Setting this will block new bucket policies that grant ' +
  'public access to buckets and objects. This setting doesn\'t change any existing policies that allow ' +
  'public access to S3 resources.';
const restrictPublicBucketsTip = 'Setting this will ignore public and cross-account access for buckets ' +
  'with policies that grant public access to buckets and objects.';

const PolicyPage = ({title, disableSubmit, setDisableSubmit, ...otherProps}: Props) => {
  const [error, setError] = useState('');
  const classes = useStyles();

  const formState = useFormState();
  const defaultPolicyString = `{
  "Version": "2012-10-17",
  "Id": "ExamplePolicy01",
  "Statement": [
    {
      "Sid": "ExampleStatement01",
      "Effect": "Allow",
      "Principal": {
        "AWS": "arn:aws:iam::123456789012:group/example-group"
      },
      "Action": [
        "s3:*"
      ],
      "Resource": [
        "arn:aws:s3:::${formState.values.name}",
        "arn:aws:s3:::${formState.values.name}/*"
      ]
    }
  ]
}`;
  let defaultToUseOnRender = defaultPolicyString;
  if (!isEmpty(formState.values.policy)) {
    defaultToUseOnRender = JSON.stringify(formState.values.policy, null, 2);
  }
  const [policyString, setPolicyString] = useState(defaultToUseOnRender);

  // parsePolicy parses the policy JSON string. It returns one of the
  // following:
  //  false: It's invalid JSON
  //  null: It's an empty policy (e.g. the user is removing a policy).
  //        It's technically not valid JSON, however, it is a valid
  //        policy.
  //  everything else: The parsed JSON policy (e.g. a JSON object).
  const parsePolicy = (value: string) => {
    let policy: any = null;
    if (value !== '') {
      try {
        policy = JSON.parse(value);
      } catch {
        policy = false;
      }
    }
    return policy;
  };

  useEffect(() => {
    const policy = formState.values.policy;
    if (!isEmpty(policy)) {
      setPolicyString(JSON.stringify(policy, null, 2));
    } else {
      onPolicyChange(defaultPolicyString);
    }
  }, []);

  useEffect(() => {
    if (formState.values.policyEnabled) {
      // The user could be re-enabling the policy. Only allow the user
      // to advance to the next page if the policy is valid.
      onPolicyChange(policyString);
    } else {
      // Always let the user advance to the next page when the policy is disabled,
      // even if the policy isn't valid JSON.
      setDisableSubmit(false);
    }
  }, [formState.values.policyEnabled]);

  const form = useForm();
  const onPolicyChange = (value: string) => {
    setPolicyString(value);
    const policy = parsePolicy(value);
    setDisableSubmit(policy === false);
    if (policy !== false) {
      setError('');
      if (policy !== undefined) {
        form.mutators.setFormValue('policy', policy);
      }
    } else {
      setError('Badly formatted JSON');
    }
  };

  return (
    <WizardPage title={title} disableSubmit={disableSubmit} {...otherProps}>
      <Checkbox name='blockPublicPolicy' label='Block Public Policies' tooltip={blockPublicPolicyTip} />
      <Checkbox name='restrictPublicBuckets' label='Restrict Public Buckets' tooltip={restrictPublicBucketsTip} />
      <Grid container>
        <Grid item xs={12} sx={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
          <Box sx={{width: '90%'}}>
            <SingleChipSelectGroupField name='policyEnabled' height='40px'
              items={[{text: 'Policy Enabled', value: true}, {text: 'Policy Disabled', value: false}]}
            />
          </Box>
        </Grid>
        {formState.values.policyEnabled &&
          <Grid item xs={12}>
            <Card>
              {error && <Typography className={classes.error}>{error}</Typography>}
              <AceEditor
                value={policyString} mode='json' theme='github' placeholder='Create Policy' fontSize={12}
                wrapEnabled={true} showPrintMargin={false} height='305px' width='100%' onChange={onPolicyChange}
                setOptions={{tabSize: 2, useWorker: false}} editorProps={{$blockScrolling: true}}
              />
            </Card>
            <Typography variant='caption' className={classes.noteText}>
              For more info on configuring a policy, see the&nbsp;
              <a
                className={classes.link} target='_blank'
                href='https://docs.aws.amazon.com/AmazonS3/latest/dev/using-with-s3-actions.html'
              >
                AWS documentation
              </a>
            </Typography>
          </Grid>}
      </Grid>
    </WizardPage>
  );
};

export default PolicyPage;
